// ScenarioDiffService — compares two scenario seeds to identify divergence
// points, shared segments, and branch differences.

import type { SimulationSeed } from '../types/index.ts';
import { branchRoutes } from '../types/index.ts';

export interface DivergencePoint {
  /** The step index where paths diverge */
  stepIndex: number;
  /** The node ID where they were last aligned (null if they diverge at step 0) */
  lastSharedNode: string | null;
  /** Node ID scenario A takes after divergence */
  nodeA: string | null;
  /** Node ID scenario B takes after divergence */
  nodeB: string | null;
}

export interface ScenarioDiff {
  scenarioA: { id: string; label: string };
  scenarioB: { id: string; label: string };
  /** Nodes shared at the start of both paths (common prefix) */
  sharedPrefix: string[];
  /** All divergence points (where paths split) */
  divergencePoints: DivergencePoint[];
  /** Nodes only in scenario A's path */
  uniqueToA: string[];
  /** Nodes only in scenario B's path */
  uniqueToB: string[];
  /** Branches activated in A but not B */
  branchesOnlyA: string[];
  /** Branches activated in B but not A */
  branchesOnlyB: string[];
  /** Branches activated in both */
  sharedBranches: string[];
  /** Total path lengths */
  pathLengthA: number;
  pathLengthB: number;
}

/**
 * Compare two scenario seeds and produce a structured diff.
 */
export function diffScenarios(seedA: SimulationSeed, seedB: SimulationSeed): ScenarioDiff {
  const pathA = seedA.expectedPath;
  const pathB = seedB.expectedPath;

  // Find shared prefix
  const sharedPrefix: string[] = [];
  const minLen = Math.min(pathA.length, pathB.length);
  let firstDivergence = minLen;
  for (let i = 0; i < minLen; i++) {
    if (pathA[i] === pathB[i]) {
      sharedPrefix.push(pathA[i]);
    } else {
      firstDivergence = i;
      break;
    }
  }

  // Find all divergence points (places where paths re-align then split again)
  const divergencePoints: DivergencePoint[] = [];
  let i = 0;
  while (i < minLen) {
    if (pathA[i] !== pathB[i]) {
      divergencePoints.push({
        stepIndex: i,
        lastSharedNode: i > 0 ? pathA[i - 1] : null,
        nodeA: pathA[i] ?? null,
        nodeB: pathB[i] ?? null,
      });
      // Skip ahead to next alignment
      i++;
      while (i < minLen && pathA[i] !== pathB[i]) {
        i++;
      }
    } else {
      i++;
    }
  }

  // If paths differ in length but aligned through minLen, that's a divergence too
  if (pathA.length !== pathB.length && firstDivergence >= minLen) {
    divergencePoints.push({
      stepIndex: minLen,
      lastSharedNode: minLen > 0 ? pathA[minLen - 1] : null,
      nodeA: pathA[minLen] ?? null,
      nodeB: pathB[minLen] ?? null,
    });
  }

  // Unique nodes
  const setA = new Set(pathA);
  const setB = new Set(pathB);
  const uniqueToA = [...setA].filter((n) => !setB.has(n));
  const uniqueToB = [...setB].filter((n) => !setA.has(n));

  // Branch differences
  const branchesA = new Set(branchRoutes(seedA.activeBranches));
  const branchesB = new Set(branchRoutes(seedB.activeBranches));
  const sharedBranches = [...branchesA].filter((b) => branchesB.has(b));
  const branchesOnlyA = [...branchesA].filter((b) => !branchesB.has(b));
  const branchesOnlyB = [...branchesB].filter((b) => !branchesA.has(b));

  return {
    scenarioA: { id: seedA.scenarioId, label: seedA.scenarioLabel },
    scenarioB: { id: seedB.scenarioId, label: seedB.scenarioLabel },
    sharedPrefix,
    divergencePoints,
    uniqueToA,
    uniqueToB,
    branchesOnlyA,
    branchesOnlyB,
    sharedBranches,
    pathLengthA: pathA.length,
    pathLengthB: pathB.length,
  };
}

export interface AnnotatedStep {
  stepIndex: number;
  nodeId: string;
  /** Which scenarios include this step at this position */
  presentIn: ('A' | 'B')[];
  /** Is this a divergence point? */
  isDivergence: boolean;
}

/**
 * Produce an annotated step-by-step view merging both paths.
 */
export function annotateSteps(seedA: SimulationSeed, seedB: SimulationSeed): AnnotatedStep[] {
  const maxLen = Math.max(seedA.expectedPath.length, seedB.expectedPath.length);
  const steps: AnnotatedStep[] = [];

  for (let i = 0; i < maxLen; i++) {
    const nodeA = seedA.expectedPath[i];
    const nodeB = seedB.expectedPath[i];

    if (nodeA === nodeB && nodeA != null) {
      steps.push({ stepIndex: i, nodeId: nodeA, presentIn: ['A', 'B'], isDivergence: false });
    } else {
      if (nodeA != null) {
        steps.push({ stepIndex: i, nodeId: nodeA, presentIn: ['A'], isDivergence: true });
      }
      if (nodeB != null) {
        steps.push({ stepIndex: i, nodeId: nodeB, presentIn: ['B'], isDivergence: true });
      }
    }
  }

  return steps;
}
